import React, { useState } from 'react';
import { Button, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DropzoneArea from '../DropzoneArea';

const RESOLUTION_LABELS = {
    0: { text: 'קיים', color: '#388e3c' },
    1: { text: 'לפי שם', color: '#1976d2' },
    2: { text: 'כרטיס חדש', color: '#f57c00' },
    3: { text: 'כרטיס חדש + מזהה חיצוני', color: '#d32f2f' },
};

const RamplusClosingBalanceImportModal = ({
    open,
    onClose,
    previewRamplusClosingBalance,
    commitRamplusClosingBalance,
    direction,
    t
}) => {
    const [balanceFile, setBalanceFile] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear() - 1);
    const [previewData, setPreviewData] = useState(null);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);

    const handlePreview = async () => {
        if (!balanceFile) return;
        try {
            setProcessing(true);
            const res = await previewRamplusClosingBalance(balanceFile, year);
            setPreviewData(res);
            setPreviewDone(true);
        } catch (err) {
            console.error('Ramplus closing balance preview failed:', err);
            setPreviewDone(false);
        } finally {
            setProcessing(false);
        }
    };

    const handleCommit = async () => {
        if (!balanceFile) return;
        try {
            setProcessing(true);
            const res = await commitRamplusClosingBalance(balanceFile, year);
            if (res?.errorMessages?.length > 0) {
                console.error('Import errors:', res.errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Ramplus closing balance import error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        setBalanceFile(null);
        setPreviewDone(false);
        setPreviewData(null);
        onClose();
    };

    const formatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    if (!open) return null;

    const entries = previewData?.entries ?? [];

    return (
        <div className="download-modal-overlay" onClick={handleClose}>
            <div className="download-modal" onClick={e => e.stopPropagation()}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: previewDone ? '1400px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">רם פלוס - יתרת סגירה</h4>
                        <IconButton className="client-back-icon" onClick={handleClose} sx={{ padding: '4px' }}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <div>
                            <h5>מאזן-רם (.xls)</h5>
                            <DropzoneArea
                                key={balanceFile ? balanceFile.name : 'ramplus-balance-empty'}
                                onDrop={files => files?.length && setBalanceFile(files[0])}
                                accept=".xls,.xlsx"
                                filesLimit={1}
                            />
                            {balanceFile && (
                                <div style={{ marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {balanceFile.name}</span>
                                </div>
                            )}
                        </div>
                        <div>
                            <h5>שנה</h5>
                            <TextField
                                type="number"
                                value={year}
                                onChange={e => setYear(parseInt(e.target.value) || new Date().getFullYear() - 1)}
                                size="small"
                                inputProps={{ min: 2000, max: 2100 }}
                                sx={{ width: 150 }}
                            />
                            <span style={{ marginRight: 10, fontSize: 13, color: '#666' }}>
                                (תאריך מסמך: 31/12/{year})
                            </span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button variant="contained" onClick={handlePreview} disabled={!balanceFile || processing}>
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!previewDone || processing}
                            onClick={handleCommit}
                        >
                            {processing ? t('Processing') : t('Import')}
                        </Button>
                    </div>

                    {previewDone && previewData && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <p style={{ fontSize: 14, color: '#666' }}>
                                שורות שנקראו: <strong>{previewData.parsedRows}</strong> |
                                {' '}שורות עם יתרה: <strong>{entries.length}</strong> |
                                {' '}כרטיסים חדשים: <strong>{previewData.newCardsToCreate}</strong>
                            </p>

                            <div style={{
                                marginBottom: 20, padding: 15, backgroundColor: '#f5f5f5',
                                borderRadius: 8, border: '1px solid #ddd', display: 'flex', gap: 30, flexWrap: 'wrap'
                            }}>
                                <div>
                                    <div style={{ fontSize: 13, color: '#666' }}>סה״כ חובה</div>
                                    <div style={{ fontSize: 18, fontWeight: 'bold', color: '#388e3c' }}>
                                        ₪{formatAmount(previewData.totalDebit)}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 13, color: '#666' }}>סה״כ זכות</div>
                                    <div style={{ fontSize: 18, fontWeight: 'bold', color: '#d32f2f' }}>
                                        ₪{formatAmount(previewData.totalCredit)}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 13, color: '#666' }}>הפרש</div>
                                    <div style={{ fontSize: 18, fontWeight: 'bold' }}>
                                        ₪{formatAmount(previewData.balance)}
                                    </div>
                                </div>
                            </div>

                            {entries.length === 0 ? (
                                <p style={{ color: '#666' }}>לא נמצאו יתרות</p>
                            ) : (
                                <table className="sortcode-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                                            <th style={{ padding: 10, border: '1px solid #ddd' }}>מפתח חשבון</th>
                                            <th style={{ padding: 10, border: '1px solid #ddd' }}>שם</th>
                                            <th style={{ padding: 10, border: '1px solid #ddd' }}>סכום</th>
                                            <th style={{ padding: 10, border: '1px solid #ddd' }}>חו/ז</th>
                                            <th style={{ padding: 10, border: '1px solid #ddd' }}>סטטוס כרטיס</th>
                                            <th style={{ padding: 10, border: '1px solid #ddd' }}>כרטיס</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entries.map((entry, i) => {
                                            const resolution = RESOLUTION_LABELS[entry.cardResolutionStatus] || { text: '?', color: '#666' };
                                            const isCredit = entry.amount > 0;
                                            return (
                                                <tr key={i} style={{
                                                    backgroundColor: entry.cardResolutionStatus >= 2 ? '#fff8e1' : i % 2 === 0 ? '#fff' : '#f9f9f9'
                                                }}>
                                                    <td style={{ padding: 10, border: '1px solid #ddd' }}>{entry.cardId}</td>
                                                    <td style={{ padding: 10, border: '1px solid #ddd' }}>{entry.cardName}</td>
                                                    <td style={{
                                                        padding: 10, border: '1px solid #ddd', textAlign: 'right',
                                                        color: isCredit ? '#d32f2f' : 'inherit',
                                                        fontWeight: isCredit ? 'bold' : 'normal'
                                                    }}>
                                                        {formatAmount(entry.amount)}
                                                    </td>
                                                    <td style={{ padding: 10, border: '1px solid #ddd' }}>
                                                        {entry.amount < 0 ? 'חובה' : 'זכות'}
                                                    </td>
                                                    <td style={{
                                                        padding: 10, border: '1px solid #ddd',
                                                        color: resolution.color, fontWeight: 'bold'
                                                    }}>
                                                        {resolution.text}
                                                    </td>
                                                    <td style={{ padding: 10, border: '1px solid #ddd' }}>
                                                        {entry.resolvedCardId || '-'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RamplusClosingBalanceImportModal;
